import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { usePromptPreview } from '@/hooks/usePromptVersions';
import { PROMPT_SECTION_LABELS, type PromptSectionName } from '@/types/api/prompt-versions';

interface PromptPreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SECTION_KEYS: (PromptSectionName | 'technical')[] = [
  'identity',
  'mission',
  'tone',
  'capabilities',
  'responseRules',
  'technical',
];

const SECTION_LABELS: Record<string, string> = {
  ...PROMPT_SECTION_LABELS,
  technical: 'Seções Técnicas (fixas)',
};

export function PromptPreviewSheet({ open, onOpenChange }: PromptPreviewSheetProps) {
  const { data: preview, isLoading, error } = usePromptPreview('draft', open);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] flex flex-col">
        <SheetHeader>
          <SheetTitle>Preview do Prompt</SheetTitle>
          <SheetDescription>
            Visualize como o prompt completo será enviado para a IA
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 py-4 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive">Erro ao carregar preview</p>
              <p className="text-sm text-muted-foreground mt-1">
                {(error instanceof Error ? error.message : undefined) || 'Tente novamente mais tarde'}
              </p>
            </div>
          ) : preview ? (
            <div className="space-y-6">
              {SECTION_KEYS.map((sectionKey) => {
                const content = preview.sections[sectionKey];
                const isTechnical = sectionKey === 'technical';

                return (
                  <div key={sectionKey} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                        {SECTION_LABELS[sectionKey]}
                      </h3>
                      {isTechnical && (
                        <Badge variant="secondary" className="text-xs">
                          Somente leitura
                        </Badge>
                      )}
                    </div>
                    <div
                      className={`p-3 rounded-lg text-sm whitespace-pre-wrap ${
                        isTechnical
                          ? 'bg-muted/30 text-muted-foreground font-mono text-xs'
                          : 'bg-muted/50'
                      }`}
                    >
                      {content || (
                        <span className="italic text-muted-foreground">
                          (vazio)
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
